import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { SegmentExportRecipient } from '@feathr/blackbox';
import { Alert, Button, UniversalSelect } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

export const DEFAULT_EXPORT_DELIVERY_SELECT_NAME = 'export-delivery-user';

export interface IExportDeliveryUserSelectProps {
  /**
   * The selected recipient. Shown as-is, not looked up in the loaded list, so the
   * field keeps showing who the export goes to while the list refetches.
   */
  value?: SegmentExportRecipient;
  onChange: (recipient: SegmentExportRecipient) => void;
  onClear: () => void;
  validationError?: string[] | string;
  /**
   * Pass `document.body` inside any modal, `allowOverflow` or not. The menu is
   * `position: fixed`, and the modal's transformed container would otherwise
   * anchor it to the wrong box, away from the control.
   */
  menuPortalTarget?: HTMLElement | null;
  name?: string;
}

/**
 * Picks the account member an export is delivered to. A select rather than a
 * text field because the completion email links to an account-scoped page, so
 * an address outside the account receives mail it cannot open — and sending
 * exported data to arbitrary addresses is a compliance problem. See
 * shrike#9142.
 *
 * Options come from `GET /segment-exports/recipients`, which anhinga builds
 * from the same membership its export create path validates against, so the
 * picker cannot offer an address the request would then reject. Deliberately
 * not `GET /users/`: this field is reachable with the `segments` permission
 * alone, and the account's users are not.
 */
function ExportDeliveryUserSelect({
  value,
  onChange,
  onClear,
  validationError,
  menuPortalTarget,
  name = DEFAULT_EXPORT_DELIVERY_SELECT_NAME,
}: Readonly<IExportDeliveryUserSelectProps>): JSX.Element {
  const { t } = useTranslation();
  const { SegmentExportRecipients } = useStore();

  /*
   * One fetch, no paging. The route accepts `page[...]` but ignores it and
   * returns every eligible recipient as a single page, so the whole list is
   * here and nothing needs `fetchMore`. See anhinga#8540.
   */
  const recipients = SegmentExportRecipients.list();

  /*
   * rachis caches a failed list, and `list()` only fetches when nothing is
   * cached, so a retry has to force the refetch. A 403 is an expected path here,
   * not just an outage: a user with no access to groups cannot load this list.
   */
  function handleRetry(): void {
    SegmentExportRecipients.list({}, { reset: true });
  }

  /*
   * The address is always part of the label: it is what the export is
   * delivered to, and two members can share a name. The name leads so the
   * route's name ordering reads naturally, and typing filters on either.
   */
  function getOptionLabel(recipient: SegmentExportRecipient): string {
    const email = recipient.get('email', '');
    const name = recipient.get('name');
    return name ? `${name} (${email})` : email;
  }

  function getOptionValue(recipient: SegmentExportRecipient): string {
    return recipient.id;
  }

  function handleSelectSingle(recipient: SegmentExportRecipient): void {
    onChange(recipient);
  }

  const isFailedLoad = recipients.isErrored && !recipients.isPending;

  return (
    <>
      <UniversalSelect<SegmentExportRecipient>
        /*
         * Opts into the guarded scroll handler, which ignores a scroll whose
         * target is the document rather than an element, and pins the menu with
         * `menuPosition: 'fixed'` so the portalled list tracks the control.
         */
        closeMenuOnScroll={true}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        helpText={t('Only users on this account can receive exports.')}
        isClearable={true}
        isLoading={recipients.isPending}
        label={t('Send export to')}
        menuPortalTarget={menuPortalTarget}
        name={name}
        onClear={onClear}
        onSelectSingle={handleSelectSingle}
        options={recipients.models}
        placeholder={t('Select a user...')}
        required={true}
        validationError={validationError}
        value={value}
      />
      {isFailedLoad && (
        <Alert
          description={t(
            'Try again. If the list still does not load, ask an account admin to check your access to groups.',
          )}
          name={'export_delivery_users_load_failed'}
          title={t("We couldn't load the users who can receive this export.")}
          type={'danger'}
        >
          <Button name={'retry_export_delivery_users'} onClick={handleRetry} type={'secondary'}>
            {t('Try again')}
          </Button>
        </Alert>
      )}
    </>
  );
}

export default observer(ExportDeliveryUserSelect);
